/**
 * 기체·증기(Gas or Vapor) 사이징.
 *
 * 근거: API Standard 520 Part I, 10판(2020) 5.6절
 *   - 5.6.2.4  식 (5)   임계유동 압력비 Pcf/P1 = (2/(k+1))^(k/(k-1))
 *   - 5.6.2.5           P2 ≤ Pcf 이면 임계유동(5.6.3), P2 > Pcf 이면 아임계(5.6.4)
 *   - 5.6.3.1.1 식 (9)  SI 임계유동 소요면적
 *   - 5.6.3.1.1 식 (13) SI 계수 C(k)
 *   - 5.6.4.1   식 (19) SI 아임계유동 소요면적
 *   - 5.6.4.1   식 (22) 아임계 유동계수 F2(k, r)
 *   - 5.6.4.3           밸런스드 벨로우즈는 아임계 조건에서도 임계유동식을 쓴다
 *
 * 단위계는 SI 로 통일한다.
 *   유량 kg/h · 온도 K · 압력 kPa(절대) · 면적 mm²
 *
 * 순수 함수만 둔다. React·DOM 을 import 하지 말 것.
 */

/** 표준 대기압 (kPa). 게이지압 ↔ 절대압 변환에 쓴다. */
export const ATMOSPHERIC_PRESSURE_KPA = 101.325;

/** 예비 사이징용 유효 배출계수 Kd (5.6.3.1.1). PRV 설치 시 0.975. */
export const KD_PRELIMINARY_PRV = 0.975;

/** 파열판 단독 사이징 시의 Kd (5.12.1.2). */
export const KD_RUPTURE_DISK_ONLY = 0.62;

/** 밸브 형식. 임계/아임계 분기와 Kb 적용 여부가 달라진다. */
export type ValveType = "conventional" | "pilot-operated" | "balanced-bellows";

export interface GasSizingInput {
  /** W — 소요 배출 유량 (kg/h) */
  readonly requiredFlowKgH: number;
  /** T — 배출 시 유입 온도 (K) */
  readonly relievingTemperatureK: number;
  /** Z — 압축계수. 값이 없으면 1.0 을 쓴다(5.6.3.2.2 b). */
  readonly compressibility: number;
  /** M — 분자량 (kg/kmol) */
  readonly molecularWeight: number;
  /** k — 이상기체 비열비 Cp/Cv (배출 온도 기준). 실기체 비열비를 쓰면 안 된다. */
  readonly specificHeatRatio: number;
  /** P1 — 배출 압력 (kPa 절대). 설정압 + 허용 과압 + 대기압. */
  readonly relievingPressureKpaAbs: number;
  /** P2 — 총 배압 (kPa 절대). 중첩배압 + 축적배압 + 대기압. */
  readonly backpressureKpaAbs: number;
  readonly valveType: ValveType;
  /** Kd — 배출계수. 생략 시 예비 사이징값 0.975. */
  readonly dischargeCoefficient?: number;
  /**
   * Kb — 배압 보정계수. **밸런스드 벨로우즈에만 적용된다**(5.6.3.1.1).
   * 제작사 자료 또는 Figure 31 에서 얻어야 하며, 프로그램이 추정하지 않는다.
   * conventional·pilot-operated 는 이 값을 무시하고 1.0 을 쓴다.
   */
  readonly bellowsBackpressureCorrection?: number;
  /**
   * Kc — 파열판 조합 보정계수 (5.6.4.1).
   * 파열판 없음 1.0 / 인증값 없는 조합 0.9.
   */
  readonly combinationCorrection?: number;
}

/** 유동 영역. */
export type FlowRegime = "critical" | "subcritical";

export interface GasSizingSuccess {
  readonly ok: true;
  /** A — 소요 유효 배출 면적 (mm²) */
  readonly requiredAreaMm2: number;
  readonly flowRegime: FlowRegime;
  /** 실제로 적용한 식 번호 (계산서 기재용) */
  readonly equation: "API 520 Part I (9)" | "API 520 Part I (19)";
  /** 계산서에 남길 중간값들 */
  readonly intermediates: {
    /** Pcf/P1 — 임계유동 압력비, 식 (5) */
    readonly criticalPressureRatio: number;
    /** Pcf — 임계유동 압력 (kPa 절대) */
    readonly criticalFlowPressureKpaAbs: number;
    /** r = P2/P1 — 배압비 */
    readonly backpressureRatio: number;
    /** C — 비열비 함수, 식 (13). 아임계식에서는 쓰이지 않아 null. */
    readonly coefficientC: number | null;
    /** F2 — 아임계 유동계수, 식 (22). 임계식에서는 쓰이지 않아 null. */
    readonly coefficientF2: number | null;
    readonly kd: number;
    readonly kb: number;
    readonly kc: number;
  };
  /** 설계자가 확인해야 할 경고. 계산은 수행됐지만 근거 확인이 필요한 항목. */
  readonly warnings: readonly string[];
}

export interface GasSizingFailure {
  readonly ok: false;
  readonly reason: "invalid-input";
  readonly message: string;
}

export type GasSizingResult = GasSizingSuccess | GasSizingFailure;

/**
 * 임계유동 압력비 Pcf/P1 — API 520 Part I 식 (5).
 *
 *   Pcf/P1 = ( 2 / (k+1) ) ^ ( k / (k-1) )
 */
export function criticalPressureRatio(k: number): number {
  return Math.pow(2 / (k + 1), k / (k - 1));
}

/**
 * 비열비 함수 C — API 520 Part I 식 (13), SI 단위.
 *
 *   C = 0.03948 · sqrt( k · ( 2/(k+1) ) ^ ( (k+1)/(k-1) ) )
 *
 * 단위: kg/(mm²·h·kPa)·sqrt(kg·kmol⁻¹·K)
 * k 를 구할 수 없는 이상기체는 보수적으로 C = 0.0239 를 쓰라고 되어 있다(5.6.3.1.1).
 */
export function coefficientC(k: number): number {
  return 0.03948 * Math.sqrt(k * Math.pow(2 / (k + 1), (k + 1) / (k - 1)));
}

/** k 를 알 수 없을 때 쓰는 보수적 C 값 (SI). 5.6.3.1.1. */
export const COEFFICIENT_C_CONSERVATIVE_SI = 0.0239;

/**
 * 아임계 유동계수 F2 — API 520 Part I 식 (22).
 *
 *   F2 = sqrt( k/(k-1) · r^(2/k) · (1 - r^((k-1)/k)) / (1 - r) )
 *
 * @param r 배압비 P2/P1
 */
export function subcriticalCoefficientF2(k: number, r: number): number {
  const a = k / (k - 1);
  const b = Math.pow(r, 2 / k);
  const c = (1 - Math.pow(r, (k - 1) / k)) / (1 - r);
  return Math.sqrt(a * b * c);
}

/**
 * 배출 압력 P1 계산 — 설정압(게이지) + 허용 과압 + 대기압.
 * 5.6.3.2.2 a) 의 `75 × 1.1 + 14.7 = 97.2 psia` 와 같은 계산.
 *
 * @param setPressureKpaGauge 설정압 (kPa 게이지)
 * @param overpressurePercent 허용 과압 (%) — 통상 10, 화재는 21
 */
export function relievingPressureKpaAbs(
  setPressureKpaGauge: number,
  overpressurePercent: number,
): number {
  return (
    setPressureKpaGauge * (1 + overpressurePercent / 100) +
    ATMOSPHERIC_PRESSURE_KPA
  );
}

function invalid(message: string): GasSizingFailure {
  return { ok: false, reason: "invalid-input", message };
}

/**
 * 기체·증기 소요 배출 면적을 계산한다.
 *
 * 분기 규칙:
 *  - 밸런스드 벨로우즈 → 배압과 무관하게 임계유동식 (9). Kb 로 아임계 영향을 반영 (5.6.4.3).
 *  - 그 외 → P2 ≤ Pcf 이면 식 (9), P2 > Pcf 이면 아임계식 (19) (5.6.2.5).
 */
export function sizeGasOrVapor(input: GasSizingInput): GasSizingResult {
  const {
    requiredFlowKgH: W,
    relievingTemperatureK: T,
    compressibility: Z,
    molecularWeight: M,
    specificHeatRatio: k,
    relievingPressureKpaAbs: P1,
    backpressureKpaAbs: P2,
    valveType,
  } = input;

  // ── 입력 검증 ────────────────────────────────────────────────
  const positives: ReadonlyArray<readonly [string, number]> = [
    ["소요 유량 W", W],
    ["배출 온도 T", T],
    ["압축계수 Z", Z],
    ["분자량 M", M],
    ["배출 압력 P1", P1],
  ];
  for (const [label, v] of positives) {
    if (!Number.isFinite(v) || v <= 0) {
      return invalid(`${label} 은(는) 0보다 큰 유한한 값이어야 합니다.`);
    }
  }
  if (!Number.isFinite(k) || k <= 1) {
    return invalid("비열비 k 는 1보다 커야 합니다.");
  }
  if (!Number.isFinite(P2) || P2 < 0) {
    return invalid("배압 P2 는 0 이상이어야 합니다.");
  }
  if (P2 >= P1) {
    return invalid(
      "배압 P2 가 배출 압력 P1 이상입니다. 이 조건에서는 밸브를 통해 유동이 발생하지 않습니다.",
    );
  }

  const Kd = input.dischargeCoefficient ?? KD_PRELIMINARY_PRV;
  const Kc = input.combinationCorrection ?? 1.0;
  if (!Number.isFinite(Kd) || Kd <= 0) return invalid("Kd 가 올바르지 않습니다.");
  if (!Number.isFinite(Kc) || Kc <= 0) return invalid("Kc 가 올바르지 않습니다.");

  const warnings: string[] = [];

  // ── Kb: 벨로우즈에만 적용 (5.6.3.1.1) ────────────────────────
  let Kb = 1.0;
  if (valveType === "balanced-bellows") {
    if (input.bellowsBackpressureCorrection === undefined) {
      Kb = 1.0;
      warnings.push(
        "밸런스드 벨로우즈 밸브인데 배압 보정계수 Kb 가 입력되지 않아 1.0 으로 계산했습니다. " +
          "실제 Kb 는 제작사 자료(또는 API 520 Part I Figure 31)에서 확인해 입력해야 합니다.",
      );
    } else {
      Kb = input.bellowsBackpressureCorrection;
      if (!Number.isFinite(Kb) || Kb <= 0 || Kb > 1) {
        return invalid("Kb 는 0 초과 1 이하이어야 합니다.");
      }
    }
  } else if (input.bellowsBackpressureCorrection !== undefined) {
    warnings.push(
      "conventional·pilot-operated 밸브에는 배압 보정계수 Kb 가 적용되지 않습니다" +
        "(API 520 Part I 5.6.3.1.1). 입력값을 무시하고 1.0 으로 계산했습니다.",
    );
  }

  // ── 임계/아임계 판별 (5.6.2.4~5) ─────────────────────────────
  const ratio = criticalPressureRatio(k);
  const Pcf = ratio * P1;
  const r = P2 / P1;

  const useCriticalEquation = valveType === "balanced-bellows" || P2 <= Pcf;
  const flowRegime: FlowRegime = P2 <= Pcf ? "critical" : "subcritical";

  if (valveType === "balanced-bellows" && flowRegime === "subcritical") {
    warnings.push(
      "배압이 임계유동 압력을 넘지만, 밸런스드 벨로우즈는 임계유동식으로 사이징합니다" +
        "(API 520 Part I 5.6.4.3). 아임계 영향은 Kb 로 반영됩니다.",
    );
  }

  if (Z === 1) {
    warnings.push("압축계수 Z 를 1.0 으로 계산했습니다. 실제 값이 있으면 입력하십시오.");
  }

  // ── 면적 계산 ───────────────────────────────────────────────
  if (useCriticalEquation) {
    // 식 (9): A = W / (C·Kd·P1·Kb·Kc) × sqrt(T·Z/M)
    const C = coefficientC(k);
    const requiredAreaMm2 =
      (W / (C * Kd * P1 * Kb * Kc)) * Math.sqrt((T * Z) / M);

    return {
      ok: true,
      requiredAreaMm2,
      flowRegime,
      equation: "API 520 Part I (9)",
      intermediates: {
        criticalPressureRatio: ratio,
        criticalFlowPressureKpaAbs: Pcf,
        backpressureRatio: r,
        coefficientC: C,
        coefficientF2: null,
        kd: Kd,
        kb: Kb,
        kc: Kc,
      },
      warnings,
    };
  }

  // 식 (19): A = 17.9·W / (F2·Kd·Kc) × sqrt( T·Z / (M·P1·(P1-P2)) )
  const F2 = subcriticalCoefficientF2(k, r);
  const requiredAreaMm2 =
    ((17.9 * W) / (F2 * Kd * Kc)) *
    Math.sqrt((T * Z) / (M * P1 * (P1 - P2)));

  return {
    ok: true,
    requiredAreaMm2,
    flowRegime,
    equation: "API 520 Part I (19)",
    intermediates: {
      criticalPressureRatio: ratio,
      criticalFlowPressureKpaAbs: Pcf,
      backpressureRatio: r,
      coefficientC: null,
      coefficientF2: F2,
      kd: Kd,
      kb: Kb,
      kc: Kc,
    },
    warnings,
  };
}
